import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { CheckCircle, XCircle, Award, Filter, Download } from 'lucide-react'

interface ExamResultRecord {
  attempt_id: string
  user_id: string
  user_name: string | null
  user_email: string
  course_title: string
  title: string
  score: number
  passing_score: number
  passed: boolean
  submitted_at: string
  has_certificate: boolean
}

export function ExamResultsManagePage() {
  useDocumentTitle('試験結果管理')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentTenant, memberships } = useTenant()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<ExamResultRecord[]>([])
  const [filterStatus, setFilterStatus] = useState<'all' | 'passed' | 'failed'>('all')

  useEffect(() => {
    const isTenantAdmin = memberships.some(
      (m) => m.tenant_id === currentTenant?.tenant_id && m.role_in_tenant === 'tenant_admin'
    )

    if (!isTenantAdmin && memberships.length > 0) {
      navigate('/mypage', { replace: true })
      return
    }

    if (user && currentTenant) {
      fetchResults()
    }
  }, [user, currentTenant])

  const fetchResults = async () => {
    if (!currentTenant) return

    try {
      setLoading(true)
      setError(null)

      // 試験結果を取得
      const { data: attemptsData, error: attemptsError } = await supabase
        .from('exam_attempts')
        .select(`
          attempt_id,
          user_id,
          score,
          passing_score,
          passed,
          submitted_at,
          exams!inner(title, courses!inner(title))
        `)
        .eq('tenant_id', currentTenant.tenant_id)
        .eq('status', 'graded')
        .order('submitted_at', { ascending: false })

      if (attemptsError) throw attemptsError

      // ユーザー情報を取得
      const userIds = [...new Set(attemptsData?.map((a: any) => a.user_id) || [])]
      const { data: usersData } = await supabase
        .from('users')
        .select('user_id, full_name, email')
        .in('user_id', userIds)

      const usersMap = new Map(usersData?.map((u) => [u.user_id, u]))

      // 認定証の有無を確認
      const attemptIds = attemptsData?.map((a: any) => a.attempt_id) || []
      const { data: certificatesData } = await supabase
        .from('certificates')
        .select('attempt_id')
        .in('attempt_id', attemptIds)

      const certificatesSet = new Set(certificatesData?.map((c) => c.attempt_id))

      // データを整形
      const resultsRecords: ExamResultRecord[] = (attemptsData || []).map((attempt: any) => {
        const user = usersMap.get(attempt.user_id)
        return {
          attempt_id: attempt.attempt_id,
          user_id: attempt.user_id,
          user_name: user?.full_name || null,
          user_email: user?.email || '',
          course_title: attempt.exams?.courses?.title || '不明',
          title: attempt.exams?.title || '不明',
          score: attempt.score,
          passing_score: attempt.passing_score,
          passed: attempt.passed,
          submitted_at: attempt.submitted_at,
          has_certificate: certificatesSet.has(attempt.attempt_id),
        }
      })

      setResults(resultsRecords)
    } catch (err: any) {
      console.error('Error fetching exam results:', err)
      setError(err.message || '試験結果の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const filteredResults = results.filter((r) => {
    if (filterStatus === 'all') return true
    if (filterStatus === 'passed') return r.passed
    if (filterStatus === 'failed') return !r.passed
    return true
  })

  const handleExportCSV = () => {
    // CSV出力のヘッダー
    const headers = [
      '受験日時',
      '受験者名',
      'メールアドレス',
      '講座名',
      '試験名',
      '得点',
      '合格点',
      '結果',
      '認定証',
    ]

    // CSV出力のデータ行
    const rows = filteredResults.map((r) => [
      new Date(r.submitted_at).toLocaleString('ja-JP'),
      r.user_name || '未設定',
      r.user_email,
      r.course_title,
      r.title,
      r.score.toString(),
      r.passing_score.toString(),
      r.passed ? '合格' : '不合格',
      r.has_certificate ? '発行済み' : '-',
    ])

    // CSVテキスト生成
    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n')

    // BOMを追加（Excel対応）
    const bom = '\uFEFF'
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' })

    // ダウンロード
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `exam_results_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="試験結果を読み込み中..." />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">試験結果管理</h1>
          <p className="mt-2 text-gray-600">受験者の試験結果を確認します</p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} onRetry={fetchResults} />
          </div>
        )}

        {/* フィルターとアクション */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">絞り込み:</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-3 py-1 rounded-md text-sm transition ${
                  filterStatus === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                全て
              </button>
              <button
                onClick={() => setFilterStatus('passed')}
                className={`px-3 py-1 rounded-md text-sm transition ${
                  filterStatus === 'passed'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                合格
              </button>
              <button
                onClick={() => setFilterStatus('failed')}
                className={`px-3 py-1 rounded-md text-sm transition ${
                  filterStatus === 'failed'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                不合格
              </button>
            </div>
          </div>

          {filteredResults.length > 0 && (
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition text-sm font-medium"
            >
              <Download className="h-4 w-4" />
              CSV出力
            </button>
          )}
        </div>

        {/* 統計サマリー */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">総受験回数</p>
                <p className="text-2xl font-bold text-gray-900">{results.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">合格回数</p>
                <p className="text-2xl font-bold text-gray-900">
                  {results.filter((r) => r.passed).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Award className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">認定証発行</p>
                <p className="text-2xl font-bold text-gray-900">
                  {results.filter((r) => r.has_certificate).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 試験結果一覧 */}
        {filteredResults.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filterStatus === 'all'
                ? '試験結果がありません'
                : filterStatus === 'passed'
                ? '合格結果がありません'
                : '不合格結果がありません'}
            </h3>
            <p className="text-gray-600">受験完了後に結果が表示されます</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    受験日時
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    受験者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    講座/試験
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    得点
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    結果
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    認定証
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResults.map((result) => (
                  <tr key={result.attempt_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(result.submitted_at).toLocaleDateString('ja-JP')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(result.submitted_at).toLocaleTimeString('ja-JP')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {result.user_name || '未設定'}
                      </div>
                      <div className="text-xs text-gray-500">{result.user_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{result.course_title}</div>
                      <div className="text-xs text-gray-500">{result.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {result.score} / 100
                      </div>
                      <div className="text-xs text-gray-500">合格点: {result.passing_score}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {result.passed ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="text-sm font-medium text-green-600">合格</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <XCircle className="h-5 w-5 text-red-600" />
                          <span className="text-sm font-medium text-red-600">不合格</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {result.has_certificate ? (
                        <div className="flex items-center gap-2">
                          <Award className="h-5 w-5 text-purple-600" />
                          <span className="text-sm text-gray-900">発行済み</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
